import { BaseComponent, ComponentConfig } from './base-component';
import { triggerEvent } from './util/event-handler';

const SELECTOR_TRACK = '.form-range-track';
const SELECTOR_FILL = '.slider-fill';
const SELECTOR_THUMB = '.slider-thumb';
const SELECTOR_LABEL = '.slider-thumb-label';
const CLASS_FOCUSED = 'slider-thumb--focused';

/**
 * Slider — makes `.form-range` markup interactive.
 *
 * The CSS ships the visual: an 8px track, a navy fill and a 24px thumb. What it cannot
 * ship is behaviour, so the markup alone renders a slider that cannot be moved. This
 * class adds dragging, keyboard control and the ARIA a screen reader needs, and keeps a
 * hidden `<input>` in sync so the value posts with a form like any other field.
 *
 * A native `<input type="range">` was the obvious alternative and does not work here:
 * its track and thumb are drawn by the browser and cannot be restyled to the Figma spec
 * without per-engine pseudo-elements, which is exactly the parallel styling layer the
 * design system exists to avoid.
 */
export class Slider extends BaseComponent {
  static get NAME() { return 'slider'; }

  static DefaultConfig: ComponentConfig = {
    min: 0,
    max: 100,
    step: 1,
    value: 0,
    disabled: false,
    /** Formats the number under the thumb. Receives the raw value. */
    format: null,
    /** Fired continuously while dragging, like a native range's `input`. */
    onInput: null,
    /** Fired once the value settles: drag release, key press, track click. */
    onChange: null,
  };

  private _thumb!: HTMLElement;
  private _fill!: HTMLElement | null;
  private _label!: HTMLElement | null;
  private _input!: HTMLInputElement | null;
  private _value = 0;
  private _dotNetRef: any = null;

  private _pointerDown!: (e: PointerEvent) => void;
  private _pointerMove!: (e: PointerEvent) => void;
  private _pointerUp!: (e: PointerEvent) => void;
  private _keydown!: (e: KeyboardEvent) => void;
  private _trackClick!: (e: PointerEvent) => void;
  private _dragging = false;

  constructor(element: Element, config: ComponentConfig = {}) {
    super(element, config);

    const el = this._element as HTMLElement;
    // The spec's control is dual-thumb, but the left handle sits at 0% in every Figma
    // variant and only the right one carries the value. So the interactive thumb is the
    // last one, and the fill runs 0 -> thumb, which is what "progress container spans
    // from left handle to right handle" reduces to when the left handle is at 0%.
    const thumbs = el.querySelectorAll(SELECTOR_THUMB);
    this._thumb = thumbs[thumbs.length - 1] as HTMLElement;
    this._fill = el.querySelector(SELECTOR_FILL);
    this._label = el.querySelector(SELECTOR_LABEL);
    // An input inside the wrapper makes the value submit with a plain HTML form and
    // gives Blazor's @bind something ordinary to attach to.
    this._input = el.querySelector('input[type="hidden"], input[type="range"]');

    if (!this._thumb) {
      throw new TypeError('Slider: .slider-thumb is required inside .form-range');
    }

    this._value = this._clamp(Number(this._config.value));

    // The thumb is the control: it takes focus and carries the ARIA, not the wrapper,
    // so a screen reader announces one slider rather than a group of divs.
    this._thumb.setAttribute('role', 'slider');
    this._thumb.setAttribute('tabindex', this._config.disabled ? '-1' : '0');
    this._thumb.setAttribute('aria-valuemin', String(this._config.min));
    this._thumb.setAttribute('aria-valuemax', String(this._config.max));
    if (this._config.disabled) {
      this._thumb.setAttribute('aria-disabled', 'true');
      // Also on the wrapper: the fill is a sibling of the thumb, so CSS can only
      // reach it from a common ancestor.
      el.setAttribute('aria-disabled', 'true');
    }

    this._pointerDown = (e: PointerEvent) => {
      if (this._config.disabled) return;
      e.preventDefault();
      this._dragging = true;
      this._thumb.setPointerCapture?.(e.pointerId);
      this._thumb.classList.add(CLASS_FOCUSED);
      this._thumb.focus();
    };

    this._pointerMove = (e: PointerEvent) => {
      if (!this._dragging) return;
      const before = this._value;
      this._setValue(this._valueFromClientX(e.clientX));
      if (this._value !== before) this._emitInput();
    };

    this._pointerUp = (e: PointerEvent) => {
      if (!this._dragging) return;
      this._dragging = false;
      this._thumb.releasePointerCapture?.(e.pointerId);
      this._thumb.classList.remove(CLASS_FOCUSED);
      this._emitChange();
    };

    // Clicking the track jumps there. Without it the only way to move the slider is to
    // grab a 24px target, which is a poor hit area on a long track.
    this._trackClick = (e: PointerEvent) => {
      if (this._config.disabled) return;
      if ((e.target as Element).closest(SELECTOR_THUMB)) return;
      const before = this._value;
      this._setValue(this._valueFromClientX(e.clientX));
      if (this._value !== before) this._emitInput();
      this._emitChange();
      this._thumb.focus();
    };

    this._keydown = (e: KeyboardEvent) => {
      if (this._config.disabled) return;
      const step = Number(this._config.step);
      const min = Number(this._config.min);
      const max = Number(this._config.max);
      // A page jump of one step would make Page Up/Down pointless on a long range.
      const page = Math.max(step, (max - min) / 10);
      let next: number | null = null;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp': next = this._value + step; break;
        case 'ArrowLeft':
        case 'ArrowDown': next = this._value - step; break;
        case 'PageUp': next = this._value + page; break;
        case 'PageDown': next = this._value - page; break;
        case 'Home': next = min; break;
        case 'End': next = max; break;
        default: return;
      }

      e.preventDefault();
      const before = this._value;
      this._setValue(next);
      if (this._value !== before) this._emitInput();
      this._emitChange();
    };

    this._thumb.addEventListener('pointerdown', this._pointerDown);
    this._thumb.addEventListener('pointermove', this._pointerMove);
    this._thumb.addEventListener('pointerup', this._pointerUp);
    this._thumb.addEventListener('pointercancel', this._pointerUp);
    this._thumb.addEventListener('keydown', this._keydown);
    el.addEventListener('pointerdown', this._trackClick);

    this._render();
  }

  /** Current value. */
  get value(): number { return this._value; }

  /** Moves the slider. Does not fire the change event - the caller already knows. */
  set value(v: number) {
    this._setValue(v);
  }

  /** Blazor hands over its component reference so keyboard and drag reach @bind-Value. */
  setDotNetRef(ref: any): void {
    this._dotNetRef = ref;
  }

  clearDotNetRef(): void {
    this._dotNetRef = null;
  }

  /**
   * Entry point for Blazor: find by id, instantiate, wire the callback.
   *
   * The element may already have an instance, because auto-init claims prerendered
   * markup on DOMContentLoaded, before Blazor's first OnAfterRenderAsync. That instance
   * was built from the data attributes, so the config is re-applied here rather than
   * dropped, which is what getOrCreateInstance alone would do.
   */
  static initWithDotNetRef(id: string, ref: any, config: ComponentConfig = {}): void {
    const el = document.getElementById(id);
    if (!el) return;
    const existing = Slider.getInstance(el) as Slider | null;
    const instance = existing ?? new Slider(el, config);
    if (existing) existing.configure(config);
    instance.setDotNetRef(ref);
  }

  /** Applies a new config and repositions. Used when Blazor initialises late. */
  configure(config: ComponentConfig): void {
    this._config = { ...this._config, ...config };
    this._thumb.setAttribute('aria-valuemin', String(this._config.min));
    this._thumb.setAttribute('aria-valuemax', String(this._config.max));
    this._thumb.setAttribute('tabindex', this._config.disabled ? '-1' : '0');
    if (config.value !== undefined) this._value = this._clamp(Number(config.value));
    this._render();
  }

  /** Blazor pushed a new value down; move the thumb without echoing back up. */
  static setValueById(id: string, value: number): void {
    const el = document.getElementById(id);
    if (!el) return;
    const instance = Slider.getInstance(el) as Slider | null;
    if (instance) instance.value = value;
  }

  static disposeById(id: string): void {
    const el = document.getElementById(id);
    if (!el) return;
    const instance = Slider.getInstance(el) as Slider | null;
    if (instance) instance.dispose();
  }

  dispose(): void {
    this._thumb?.removeEventListener('pointerdown', this._pointerDown);
    this._thumb?.removeEventListener('pointermove', this._pointerMove);
    this._thumb?.removeEventListener('pointerup', this._pointerUp);
    this._thumb?.removeEventListener('pointercancel', this._pointerUp);
    this._thumb?.removeEventListener('keydown', this._keydown);
    (this._element as HTMLElement)?.removeEventListener('pointerdown', this._trackClick);
    this.clearDotNetRef();
    super.dispose();
  }

  private _clamp(v: number): number {
    const min = Number(this._config.min);
    const max = Number(this._config.max);
    const step = Number(this._config.step);
    if (Number.isNaN(v)) return min;

    const stepped = step > 0 ? Math.round((v - min) / step) * step + min : v;
    // Floating point: 0.1 steps otherwise produce 1.2000000000000002 under the thumb.
    const decimals = (String(step).split('.')[1] || '').length;
    const rounded = decimals > 0 ? Number(stepped.toFixed(decimals)) : stepped;
    return Math.min(max, Math.max(min, rounded));
  }

  private _valueFromClientX(clientX: number): number {
    const track = (this._element as HTMLElement).querySelector(SELECTOR_TRACK)
      ?? this._element as HTMLElement;
    const rect = (track as HTMLElement).getBoundingClientRect();
    if (rect.width === 0) return this._value;
    const min = Number(this._config.min);
    const max = Number(this._config.max);
    const ratio = (clientX - rect.left) / rect.width;
    return this._clamp(min + ratio * (max - min));
  }

  private _setValue(v: number): void {
    const next = this._clamp(v);
    if (next === this._value) return;
    this._value = next;
    this._render();
  }

  private _render(): void {
    const min = Number(this._config.min);
    const max = Number(this._config.max);
    const percent = max === min ? 0 : ((this._value - min) / (max - min)) * 100;

    this._thumb.style.left = `${percent}%`;
    if (this._fill) this._fill.style.width = `${percent}%`;
    if (this._input) this._input.value = String(this._value);

    this._thumb.setAttribute('aria-valuenow', String(this._value));
    const text = this._formatValue();
    this._thumb.setAttribute('aria-valuetext', text);
    if (this._label) this._label.textContent = text;
  }

  private _formatValue(): string {
    const fmt = this._config.format;
    return typeof fmt === 'function' ? String((fmt as Function)(this._value)) : String(this._value);
  }

  /**
   * Live update while the value is still moving, mirroring the native range input's
   * `input` event. A label that only caught up on release would look frozen mid-drag.
   */
  private _emitInput(): void {
    if (!this._element) return;
    triggerEvent(this._element, 'antumo:slider:input', { value: this._value }, false);
    if (typeof this._config.onInput === 'function') {
      (this._config.onInput as Function)(this._value);
    }
    if (this._dotNetRef) {
      this._dotNetRef.invokeMethodAsync('OnSliderInput', this._value);
    }
  }

  private _emitChange(): void {
    // dispose() nulls the element; a queued pointerup can still land here afterwards.
    if (!this._element) return;
    triggerEvent(this._element, 'antumo:slider:change', { value: this._value }, false);
    if (typeof this._config.onChange === 'function') {
      (this._config.onChange as Function)(this._value);
    }
    if (this._dotNetRef) {
      this._dotNetRef.invokeMethodAsync('OnSliderChanged', this._value);
    }
    // Fires for @bind on the hidden input and for any form listening for a change.
    this._input?.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
